<template>
  <div class="page-container">
    <div class="content-wrap">
      <TopNavBar v-bind:secondTierLoadingDone="true"/>

      <b-container class="helfen-container">
        <!-- Login wall -->
        <div v-if="!isLoggedIn()" class="helfen-login-wall text-center mt-5 pt-5">
          <h1 class="text-primary">{{ $t('helfen.title') }}</h1>
          <p class="text-muted">{{ $t('helfen.login_required') }}</p>
          <router-link :to="{name: 'auth.login'}" class="btn btn-success mt-3">{{ $t('Go to login') }}</router-link>
          <router-link :to="{name: 'auth.register'}" class="btn btn-outline-primary mt-3 ml-2">{{ $t('Go to register') }}</router-link>
        </div>

        <!-- Loading -->
        <div v-else-if="loading && !currentEntry" class="text-center mt-5 pt-5">
          <b-spinner variant="primary"></b-spinner>
          <p class="mt-3 text-muted">{{ $t('helfen.loading') }}</p>
        </div>

        <!-- Load error -->
        <div v-else-if="loadError" class="text-center mt-5 pt-5">
          <h2 class="text-primary">{{ $t('helfen.load_error') }}</h2>
          <p class="text-muted">{{ $t('helfen.load_error_hint') }}</p>
          <button type="button" class="btn btn-success mt-3" @click="retryLoad">{{ $t('helfen.retry') }}</button>
          <router-link :to="{name: 'home'}" class="btn btn-outline-primary mt-3 ml-2">{{ $t('helfen.back_to_dict') }}</router-link>
        </div>

        <!-- Empty queue -->
        <div v-else-if="!currentEntry && !loading" class="text-center mt-5 pt-5">
          <h2 class="text-primary">{{ $t('helfen.empty_title') }}</h2>
          <p class="text-muted">{{ $t('helfen.empty_body') }}</p>
          <router-link :to="{name: 'home'}" class="btn btn-primary mt-3">{{ $t('helfen.back_to_dict') }}</router-link>
        </div>

        <!-- Active session -->
        <div v-else-if="!sessionComplete">
          <header class="session-header">
            <h1>{{ $t('helfen.title') }}</h1>
            <p>{{ $t('helfen.subtitle') }}</p>
            <div class="session-meta">
              <div class="progress-dots" aria-hidden="true">
                <span
                  v-for="i in sessionSize"
                  :key="'dot-' + i"
                  :class="dotClass(i)"
                ></span>
              </div>
              <span class="session-count">{{ sessionProgressLabel }}</span>
              <button type="button" class="btn-text" @click="finishSession">{{ $t('helfen.finish') }}</button>
            </div>
          </header>

          <div class="card-wrap">
            <article
              :class="['dict-card helfen-card', cardAnimation]"
              v-if="currentEntry"
            >
              <div class="helfen-card__hint">
                <i class="fas fa-search helfen-card__hint-icon" aria-hidden="true"></i>
                <span v-if="searchDemandPerYear > 0">
                  {{ $t('helfen.card_hint_demand', { count: searchDemandPerYear }) }}
                </span>
                <span v-else>{{ $t('helfen.card_hint') }}</span>
              </div>
              <helfen-mapping-rows
                :entry="currentEntry"
                :singular-neutral="singularNeutral"
                :plural-neutral="pluralNeutral"
                :alternative-neutral="alternativeNeutral"
                :singular-placeholder="singularPlaceholder"
                :plural-placeholder="pluralPlaceholder"
                :term-placeholder="$t('helfen.placeholder_term')"
                @update:singularNeutral="singularNeutral = $event"
                @update:pluralNeutral="pluralNeutral = $event"
                @update:alternativeNeutral="alternativeNeutral = $event"
                @submit="submitSuggestion"
              />

              <div class="helfen-existing" v-if="existingSuggestions.length">
                <div class="helfen-existing__label">{{ $t('helfen.existing') }}</div>
                <ul class="helfen-existing__list">
                  <li
                    v-for="(suggestion, idx) in existingSuggestions"
                    :key="'s-' + idx"
                    :class="{ 'helfen-existing__item--notation': suggestion.notation }"
                  >
                    <span class="helfen-existing__mark" aria-hidden="true">
                      {{ suggestion.notation ? 'ℹ' : (suggestion.verified ? '✓' : '?') }}
                    </span>
                    <span class="helfen-existing__text">{{ suggestion.text }}</span>
                    <span v-if="suggestion.notation" class="helfen-existing__tag">{{ $t('dict.notation_short') }}</span>
                    <span v-else class="helfen-existing__votes">{{ suggestion.votes }}</span>
                  </li>
                </ul>
              </div>
              <p v-else class="helfen-existing__empty">{{ $t('helfen.no_existing') }}</p>

              <div class="helfen-card__actions">
                <b-alert v-if="error" show variant="danger" class="mb-2">{{ error }}</b-alert>
                <button
                  type="button"
                  class="btn-primary-action"
                  :disabled="submitting"
                  @click="submitSuggestion"
                >
                  {{ submitting ? '…' : $t('helfen.submit') }}
                </button>
                <div class="card-actions-row">
                  <button type="button" class="btn-skip" @click="skipEntry" :disabled="submitting">
                    ← {{ $t('helfen.skip') }}
                  </button>
                </div>
              </div>
            </article>

            <div :class="['success-flash', showSuccessFlash ? 'show' : '']">
              <div class="icon">✓</div>
              <p>{{ $t('helfen.saved') }}</p>
            </div>
          </div>
        </div>

        <!-- Session complete -->
        <div v-else class="end-screen">
          <div class="end-screen__icon" aria-hidden="true">✓</div>
          <h2>{{ $t('helfen.thanks') }}</h2>
          <p class="stat">{{ contributedTerms.length }} {{ contributedTerms.length === 1 ? $t('helfen.term_one') : $t('helfen.term_many') }}</p>
          <p class="text-muted">{{ $t('helfen.pending_review') }}</p>
          <ul class="terms" v-if="contributedTerms.length">
            <li v-for="term in contributedTerms" :key="term.search">
              <router-link :to="{ name: 'home', query: { search: term.search } }">{{ term.label }}</router-link>
            </li>
          </ul>
          <div class="end-actions">
            <button type="button" class="btn-primary-action" @click="restartSession">{{ $t('helfen.more_time') }}</button>
            <router-link :to="{name: 'home'}" class="btn-outline-link">{{ $t('helfen.back_to_dict') }}</router-link>
          </div>
        </div>
      </b-container>
    </div>
    <GenderAppFooter/>
  </div>
</template>

<script>
import TopNavBar from '../../components/TopNavBar'
import HelfenMappingRows from '../../components/HelfenMappingRows'
import GenderAppFooter from '../../components/Footer'
import { modalMixin } from '../../mixins/modalMixin'
import { DictionaryEntryService } from '../../services/dictionaryEntryService'
import { AuthenticatedDictionaryEntryService } from '../../services/authenticatedDictionaryEntryService'
import { genderedSearchChip } from '../../utils/genderedFormLabel'
import { helfenSearchesPerYear } from '../../utils/searchDemand'

const SESSION_SIZE = 8

export default {
  name: 'Helfen',
  components: { TopNavBar, HelfenMappingRows, GenderAppFooter },
  mixins: [modalMixin],
  metaInfo: {
    meta: [
      { name: 'robots', content: 'noindex, nofollow' }
    ]
  },
  data () {
    return {
      sessionSize: SESSION_SIZE,
      cardIndex: 0,
      queue: [],
      skippedIds: [],
      contributedTerms: [],
      sessionComplete: false,
      loading: false,
      loadError: false,
      submitting: false,
      error: '',
      alternativeNeutral: '',
      singularNeutral: '',
      pluralNeutral: '',
      cardAnimation: '',
      showSuccessFlash: false
    }
  },
  computed: {
    currentEntry () {
      return this.queue[this.cardIndex] || null
    },
    sessionProgressLabel () {
      const n = Math.min(this.cardIndex + 1, this.sessionSize)
      return `${n} ${this.$t('helfen.of')} ${this.sessionSize}`
    },
    isTermOnly () {
      const e = this.currentEntry
      if (!e) return false
      const hasGenderedForms = !!(
        e.singular_masculine || e.singular_feminine ||
        e.plural_masculine || e.plural_feminine
      )
      return !!e.term && !hasGenderedForms
    },
    showSingularInput () {
      const e = this.currentEntry
      if (!e || this.isTermOnly) return false
      return !!(e.singular_masculine || e.singular_feminine)
    },
    showPluralInput () {
      const e = this.currentEntry
      if (!e || this.isTermOnly) return false
      return !!(e.plural_masculine || e.plural_feminine)
    },
    singularPlaceholder () {
      const e = this.currentEntry
      if (!e) return ''
      if (e.singular_masculine && e.singular_feminine) {
        return this.$t('helfen.placeholder_pair', {
          m: e.singular_masculine,
          f: e.singular_feminine
        })
      }
      return e.singular_masculine || e.singular_feminine || this.$t('Enter singular gender neutral')
    },
    pluralPlaceholder () {
      const e = this.currentEntry
      if (!e) return ''
      if (e.plural_masculine && e.plural_feminine) {
        return this.$t('helfen.placeholder_pair', {
          m: e.plural_masculine,
          f: e.plural_feminine
        })
      }
      return e.plural_masculine || e.plural_feminine || this.$t('Enter plural gender neutral')
    },
    existingSuggestions () {
      const e = this.currentEntry
      if (!e) return []
      const items = []
      const votes = (e.total_likes || 0) - (e.total_dislikes || 0)
      if (e.type !== 'neut') {
        if (e.alternative) {
          items.push({ text: e.alternative, verified: e.is_verified, votes, notation: true })
        }
        if (e.singular_gender_neutral) {
          items.push({ text: e.singular_gender_neutral, verified: e.is_verified, votes, notation: true })
        }
        if (e.plural_gender_neutral) {
          items.push({ text: e.plural_gender_neutral, verified: e.is_verified, votes, notation: true })
        }
      } else {
        if (e.alternative) {
          items.push({ text: e.alternative, verified: e.is_verified, votes })
        }
        if (e.singular_gender_neutral || e.plural_gender_neutral) {
          const text = [e.singular_gender_neutral, e.plural_gender_neutral].filter(Boolean).join(' · ')
          if (text && !items.some(i => i.text === text)) {
            items.push({ text, verified: e.is_verified, votes })
          }
        }
      }
      const grouped = e.group_neutral_suggestions || []
      grouped.forEach(suggestion => {
        if (!items.some(i => i.text === suggestion.text)) {
          items.push(suggestion)
        }
      })
      return items
    },
    searchDemandCount () {
      const e = this.currentEntry
      if (!e) return 0
      return e.search_demand != null ? e.search_demand : (e.website_search_count || 0)
    },
    searchDemandPerYear () {
      return helfenSearchesPerYear(this.searchDemandCount)
    }
  },
  methods: {
    dotClass (i) {
      const idx = i - 1
      if (idx < this.contributedTerms.length) return 'dot done'
      if (idx === this.cardIndex) return 'dot current'
      return 'dot'
    },
    seenIds () {
      const ids = this.skippedIds.slice()
      this.queue.forEach((entry, idx) => {
        if (idx <= this.cardIndex && entry && entry.id) ids.push(entry.id)
      })
      return [...new Set(ids)]
    },
    async loadQueue () {
      this.loading = true
      this.error = ''
      this.loadError = false
      try {
        const response = await DictionaryEntryService.getContributeQueue(this.seenIds(), this.sessionSize + 2)
        const results = response.data.results || []
        if (!results.length && !this.queue.length) {
          this.queue = []
        } else if (results.length) {
          const existingIds = new Set(this.queue.map(e => e.id))
          results.forEach(entry => {
            if (!existingIds.has(entry.id)) {
              this.queue.push(entry)
            }
          })
        }
      } catch (err) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    retryLoad () {
      this.loadError = false
      this.loadQueue()
    },
    buildPayload () {
      const e = this.currentEntry
      const base = {
        type: 'neut',
        example: ''
      }
      if (this.isTermOnly) {
        return {
          ...base,
          term: e.term,
          alternative: this.alternativeNeutral.trim(),
          singular_gender_neutral: '',
          plural_gender_neutral: ''
        }
      }
      return {
        ...base,
        alternative: '',
        term: e.term || '',
        singular_gender_neutral: this.showSingularInput ? this.singularNeutral.trim() : '',
        plural_gender_neutral: this.showPluralInput ? this.pluralNeutral.trim() : '',
        singular_masculine: e.singular_masculine || '',
        singular_feminine: e.singular_feminine || '',
        plural_masculine: e.plural_masculine || '',
        plural_feminine: e.plural_feminine || ''
      }
    },
    async submitSuggestion () {
      if (!this.currentEntry || this.submitting) return
      const hasInput = this.isTermOnly
        ? !!this.alternativeNeutral.trim()
        : (
          (this.showSingularInput && !!this.singularNeutral.trim()) ||
          (this.showPluralInput && !!this.pluralNeutral.trim())
        )
      if (!hasInput) {
        this.error = this.$t('helfen.need_input')
        return
      }
      this.submitting = true
      this.error = ''
      try {
        const response = await DictionaryEntryService.create(this.buildPayload())
        if (response.data && response.data.id) {
          AuthenticatedDictionaryEntryService.like(response.data.id).catch(() => {})
        }
        this.contributedTerms.push(genderedSearchChip(this.currentEntry))
        this.showSuccessFlash = true
        this.cardAnimation = 'slide-up'
        setTimeout(() => {
          this.showSuccessFlash = false
          this.advanceCard()
        }, 550)
      } catch (err) {
        const msg = err.message && err.message.error ? err.message.error : this.$t('helfen.submit_error')
        this.error = msg
        this.submitting = false
      }
    },
    skipEntry () {
      if (!this.currentEntry || this.submitting) return
      this.skippedIds.push(this.currentEntry.id)
      this.cardAnimation = 'slide-left'
      setTimeout(() => this.advanceCard(), 350)
    },
    advanceCard () {
      this.alternativeNeutral = ''
      this.singularNeutral = ''
      this.pluralNeutral = ''
      this.submitting = false
      this.cardAnimation = ''
      this.cardIndex++
      if (this.cardIndex >= this.sessionSize) {
        this.sessionComplete = true
        return
      }
      if (this.cardIndex >= this.queue.length) {
        this.loadQueue().then(() => {
          if (!this.queue[this.cardIndex]) {
            this.sessionComplete = true
          }
        })
      }
    },
    finishSession () {
      this.sessionComplete = true
    },
    restartSession () {
      this.cardIndex = 0
      this.queue = []
      this.skippedIds = []
      this.contributedTerms = []
      this.sessionComplete = false
      this.loadQueue()
    }
  },
  mounted () {
    if (this.isLoggedIn()) {
      this.loadQueue()
    }
  }
}
</script>

<style scoped>
.helfen-container {
  max-width: 640px;
  padding: 5rem 1rem 3rem;
}

.session-header {
  text-align: center;
  margin-bottom: 1.5rem;
}
.session-header h1 {
  margin: 0 0 0.25rem;
  font-size: 1.75rem;
  color: #243A51;
}
.session-header p {
  margin: 0;
  color: #777;
  font-size: 0.95rem;
}
.session-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1.25rem;
  gap: 1rem;
}
.progress-dots {
  display: flex;
  gap: 0.35rem;
  flex: 1;
}
.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #dee2e6;
  transition: background 0.2s;
}
.dot.done { background: #22B24C; }
.dot.current { background: #243A51; transform: scale(1.15); }
.session-count {
  font-size: 0.85rem;
  color: #58585A;
  white-space: nowrap;
}
.btn-text {
  background: none;
  border: none;
  color: #777;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
}
.btn-text:hover { color: #243A51; }

.card-wrap {
  position: relative;
  min-height: 380px;
}
.helfen-card {
  transition: transform 0.35s ease, opacity 0.35s ease;
}
.helfen-card__hint {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem 0.5rem;
  padding: 0.55rem 1rem;
  background: #f0f7f3;
  border-bottom: 1px solid #e0ebe4;
  font-size: 0.78rem;
  line-height: 1.35;
  color: #3d5c48;
}
.helfen-card__hint-icon {
  font-size: 0.72rem;
  color: #22B24C;
  opacity: 0.9;
}
.helfen-card.slide-left {
  transform: translateX(-120%) rotate(-4deg);
  opacity: 0;
}
.helfen-card.slide-up {
  transform: translateY(-80px) scale(0.96);
  opacity: 0;
}

.helfen-card__actions {
  padding: 0 1rem 1rem;
}
.btn-primary-action {
  width: 100%;
  padding: 0.85rem 1rem;
  background: #22B24C;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
}
.btn-primary-action:hover { background: #1a943d; }
.btn-primary-action:disabled { background: #ccc; cursor: not-allowed; }
.card-actions-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.65rem;
}
.btn-skip {
  background: none;
  border: none;
  color: #777;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0.5rem;
}
.btn-skip:hover { color: #243A51; }
.keyboard-hint { font-size: 0.72rem; color: #aaa; }
kbd {
  background: #eee;
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 0.1rem 0.35rem;
  font-size: 0.7rem;
}

.success-flash {
  display: none;
  position: absolute;
  inset: 0;
  background: rgba(34, 178, 76, 0.08);
  border-radius: 6px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  z-index: 10;
}
.success-flash.show { display: flex; }
.success-flash .icon {
  font-size: 3rem;
  color: #22B24C;
  margin-bottom: 0.5rem;
}
.success-flash p {
  margin: 0;
  font-weight: 600;
  color: #22B24C;
}

.end-screen {
  text-align: center;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  box-shadow: 0 2px 12px rgba(36, 58, 81, 0.12);
  padding: 2.5rem 1.5rem;
  margin-top: 2rem;
}
.end-screen__icon {
  width: 2.75rem;
  height: 2.75rem;
  margin: 0 auto 0.5rem;
  border-radius: 50%;
  background: #22B24C;
  color: #fff;
  font-size: 1.35rem;
  line-height: 2.75rem;
  font-weight: 600;
}
.end-screen h2 { margin: 0 0 0.5rem; color: #243A51; }
.end-screen .stat {
  font-size: 1.5rem;
  font-weight: 700;
  color: #22B24C;
  margin: 0.5rem 0;
}
.end-screen .terms {
  list-style: none;
  padding: 0;
  margin: 1.25rem 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
}
.end-screen .terms li {
  background: #eef4f8;
  border-radius: 999px;
  font-size: 0.85rem;
}

.end-screen .terms a {
  display: block;
  color: #243A51;
  padding: 0.35rem 0.75rem;
  text-decoration: none;
}

.end-screen .terms a:hover {
  color: #243A51;
  text-decoration: underline;
}
.end-actions {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  max-width: 280px;
  margin: 1.5rem auto 0;
}
.btn-outline-link {
  display: block;
  padding: 0.75rem 1rem;
  background: #fff;
  color: #243A51;
  border: 2px solid #243A51;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
}
.btn-outline-link:hover {
  background: #eef4f8;
  color: #243A51;
  text-decoration: none;
}
</style>
